// ╔══════════════════════════════════════════════════════════════════════════╗
// ║ OUTBOX — fila de escrita durável (IndexedDB) para o chão do evento.       ║
// ║                                                                          ║
// ║ O Wi-Fi/4G de um evento satura. Até aqui, um tap sem rede desaparecia em  ║
// ║ silêncio (`nfcTap(...).catch(() => {})`) — e um tap perdido é um nó       ║
// ║ perdido no grafo, que é a matéria-prima do matchmaking.                   ║
// ║                                                                          ║
// ║ Contrato: escrever é ENFILEIRAR. A operação sobrevive a fechar a app, a   ║
// ║ ficar sem rede e a recarregar a página; drena quando a rede volta. Cada   ║
// ║ operação leva um UUID gerado aqui — é a chave de idempotência que o       ║
// ║ servidor usa (migração 069) para que reenviar N vezes grave 1 linha.      ║
// ║                                                                          ║
// ║ Só o TAP passa por aqui. A ligação precisa de feedback imediato (é um    ║
// ║ pedido com consentimento) e o check-in é online por desenho (antifraude). ║
// ║ Não é uma sincronização offline da app inteira — leituras continuam a ir  ║
// ║ à rede e a falhar visivelmente, que é o correto.                          ║
// ╚══════════════════════════════════════════════════════════════════════════╝
import { ApiError } from '@/services/http';

// Só tipos com sender registado (outbox-senders.ts). Antes declarava também
// 'connect' e 'checkin' sem sender nem quem os enfileirasse: um contrato que prometia mais do que cumpria.
export type OutboxKind = 'tap';

export interface OutboxOp<P = unknown> {
  /** UUID gerado no cliente = chave de idempotência enviada ao servidor. */
  id: string;
  kind: OutboxKind;
  payload: P;
  createdAt: number;
  attempts: number;
  lastError?: string;
}

const DB_NAME = 'marcianus-outbox';
const STORE = 'ops';
const DB_VERSION = 1;

/** Um envio que falha assim tantas vezes não é rede — é um contrato partido.
 *  Paramos de tentar, mas NÃO deitamos fora: a operação fica visível na fila. */
const MAX_ATTEMPTS = 8;

// ── Recibos de recusa ──
// Uma operação que o servidor recusa de forma definitiva sai da fila (reenviar seria
// um ciclo infinito) — mas já não desaparece sem rasto: fica um recibo mínimo (sem
// payload, sem dados pessoais) para diagnóstico e para a UI poder avisar.
const REJECTED_KEY = 'marc:outbox:rejected';
const REJECTED_MAX = 20;
export interface RejectedReceipt { id: string; kind: OutboxKind; status: number; at: number }

export function getRejected(): RejectedReceipt[] {
  try { return JSON.parse(localStorage.getItem(REJECTED_KEY) || '[]') as RejectedReceipt[]; } catch { return []; }
}
function recordRejected(op: OutboxOp, err: ApiError): void {
  try {
    const next = [{ id: op.id, kind: op.kind, status: err.status, at: Date.now() }, ...getRejected()].slice(0, REJECTED_MAX);
    localStorage.setItem(REJECTED_KEY, JSON.stringify(next));
  } catch { /* sem storage: o recibo é best-effort, a baixa continua */ }
}

// ── IndexedDB (sem dependências: são ~40 linhas e evitam um pacote no bundle) ──

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('createdAt', 'createdAt');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = fn(t.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

const allOps = () => tx<OutboxOp[]>('readonly', (s) => s.getAll() as IDBRequest<OutboxOp[]>);
const putOp = (op: OutboxOp) => tx('readwrite', (s) => s.put(op));
const deleteOp = (id: string) => tx('readwrite', (s) => s.delete(id));

// ── Senders: cada módulo declara COMO se envia o seu tipo de operação. ──
// O outbox não conhece endpoints; se um tipo ainda não tem sender registado
// (ex.: chunk por carregar), a operação fica na fila em vez de se perder.

type Sender<P = never> = (payload: P, opId: string) => Promise<unknown>;
const senders = new Map<OutboxKind, Sender<never>>();

export function registerSender<P>(kind: OutboxKind, fn: Sender<P>): void {
  senders.set(kind, fn as Sender<never>);
}

// ── Estado observável (para a UI mostrar "3 por sincronizar") ──

let pending = 0;
let syncing = false;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());

export function subscribeOutbox(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

let snapshot = { pending: 0, syncing: false };
export function getOutboxSnapshot() {
  return snapshot;
}

async function refreshCount() {
  const ops = await allOps().catch(() => [] as OutboxOp[]);
  pending = ops.length;
  snapshot = { pending, syncing };
  emit();
}

// ── API pública ──

/** Enfileira uma escrita crítica. Devolve o UUID (= chave de idempotência).
 *  Resolve assim que a operação está DURÁVEL em disco — não espera pela rede. */
export async function enqueue<P>(kind: OutboxKind, payload: P): Promise<string> {
  const op: OutboxOp<P> = {
    id: crypto.randomUUID(),
    kind,
    payload,
    createdAt: Date.now(),
    attempts: 0,
  };
  await putOp(op as OutboxOp);
  await refreshCount();
  // Tenta já: com rede, o utilizador nem dá pela existência da fila.
  void drain();
  return op.id;
}

/** Um erro do servidor que se repetir dá sempre o mesmo resultado — reenviar não
 *  ajuda. 408/425/429 são exceções: são "tenta outra vez", não "não". E 409 no
 *  check-in significa "já estava feito" — para a fila, isso é sucesso. */
function isTerminal(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;               // erro de rede → retry
  if ([408, 425, 429].includes(err.status)) return false;
  return err.status >= 400 && err.status < 500;
}

let draining: Promise<void> | null = null;

/** Drena a fila por ordem de chegada. Serial de propósito: a ordem dos taps é
 *  informação (a sequência de 3 taps depende dela) e uma rajada paralela numa
 *  rede saturada só piora a rede. */
export function drain(): Promise<void> {
  if (draining) return draining;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return Promise.resolve();

  draining = (async () => {
    syncing = true;
    snapshot = { pending, syncing };
    emit();
    try {
      const ops = (await allOps()).sort((a, b) => a.createdAt - b.createdAt);
      for (const op of ops) {
        if (op.attempts >= MAX_ATTEMPTS) continue;
        const send = senders.get(op.kind);
        if (!send) continue;                                   // sender ainda não registado → fica na fila

        try {
          await (send as Sender<unknown>)(op.payload, op.id);
          await deleteOp(op.id);                               // confirmado pelo servidor → dar baixa
        } catch (err) {
          if (isTerminal(err)) {
            // O servidor recusou de forma definitiva (ex.: sem bilhete para o
            // evento). Reenviar era um ciclo infinito — a operação sai da fila.
            if (err instanceof ApiError) recordRejected(op, err);
            await deleteOp(op.id);
            continue;
          }
          // Rede ou 5xx: guarda o progresso e PÁRA. Se a rede caiu, as seguintes
          // vão falhar na mesma; martelar só gasta bateria.
          op.attempts += 1;
          op.lastError = err instanceof Error ? err.message : String(err);
          await putOp(op);
          break;
        }
      }
    } finally {
      syncing = false;
      draining = null;
      await refreshCount();
    }
  })();

  return draining;
}

/** Só para testes: esvazia a fila e os senders sem recriar o módulo (recriar
 *  deixaria a ligação IndexedDB anterior aberta a bloquear o deleteDatabase). */
export async function __resetForTests(): Promise<void> {
  senders.clear();
  draining = null;
  await tx('readwrite', (s) => s.clear());
  await refreshCount();
}

// ── Gatilhos ──
// Sem setInterval cego (o da StatusBar legada acordava a app de 30 em 30s mesmo
// em segundo plano, a queimar bateria). Drenamos quando há razão para drenar:
// a rede voltou, ou o utilizador voltou à app.

let started = false;

export function startOutbox(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  window.addEventListener('online', () => void drain());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void drain();
  });

  void refreshCount().then(() => drain());
}
